import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UnderaiterMark, LogoIcon } from '@/components/brand/Marks';
import axios from 'axios';
import {
  AlertTriangle, Shield, Zap, Play, Square, RotateCcw,
  CheckCircle, DollarSign, FileText, Clock, ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const api = axios.create({ baseURL: API, withCredentials: true });

const C = { navy: '#0D1B2A', red: '#E24B4A', green: '#1D9E75', amber: '#E8A020', teal: '#00B4D8', slate: '#5C6B7A', bg: '#F5F7FA', saffron: '#FF9933' };

const PRESETS = [
  { name: 'QLD Cyclone — Cairns Region', postcodes: ['4870','4871','4868','4810','4811','4812'], authority: 25000 },
  { name: 'NSW Flood — Northern Rivers', postcodes: ['2480','2481','2482','2340','2341'], authority: 25000 },
  { name: 'VIC Storm — Gippsland', postcodes: ['3844','3840','3842','3280','3282'], authority: 25000 },
];

function fmt$(v) { if (!v) return '$0'; if (v >= 1e6) return `$${(v/1e6).toFixed(1)}M`; if (v >= 1e3) return `$${(v/1e3).toFixed(0)}K`; return `$${Math.round(v).toLocaleString()}`; }

export default function CATDemoPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(true);
  const [activating, setActivating] = useState(false);
  const [flooding, setFlooding] = useState(false);
  const [floodResult, setFloodResult] = useState(null);
  const [brand, setBrand] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get('/cat/brand').then(r => setBrand(r.data)).catch(() => {});
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get('/demo/cat/status');
      setStatus(r.data);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) setAuthed(false);
      console.error('CAT status error:', err?.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Poll while active
  useEffect(() => {
    if (status?.status === 'active' && !pollRef.current) {
      pollRef.current = setInterval(loadStatus, 3000);
    }
    if (status?.status !== 'active' && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [status?.status, loadStatus]);

  const activate = async (preset) => {
    setActivating(true);
    setFloodResult(null);
    await api.post('/demo/cat/activate', { event_name: preset.name, affected_postcodes: preset.postcodes, elevated_authority: preset.authority });
    await loadStatus();
    setActivating(false);
  };

  const flood = async (count) => {
    setFlooding(true);
    const r = await api.post(`/demo/cat/flood?count=${count}`);
    setFloodResult(r.data);
    await loadStatus();
    setFlooding(false);
  };

  const reset = async () => {
    await api.post('/demo/cat/reset');
    setFloodResult(null);
    await loadStatus();
  };

  const endEvent = async () => {
    await api.post('/demo/cat/deactivate');
    setFloodResult(null);
    await loadStatus();
  };

  const downloadReport = () => {
    window.open(`${API}/demo/cat/report`, '_blank');
  };

  const isActive = status?.status === 'active';
  const stats = status?.live_stats || status?.stats || {};

  return (
    <div className="min-h-screen" style={{ background: C.navy }} data-testid="cat-demo-page">
      {!loading && !authed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(13,27,42,0.92)' }} data-testid="cat-demo-auth-gate">
          <div className="text-center max-w-sm px-6">
            <Shield className="w-10 h-10 mx-auto mb-3" style={{ color: C.teal }} />
            <h2 className="text-lg font-bold text-white mb-2">Admin sign-in required</h2>
            <p className="text-sm mb-5" style={{ color: '#8896A8' }}>The CAT simulator writes live claims into the platform, so it's restricted to MGA admins.</p>
            <button onClick={() => navigate('/login')} data-testid="cat-demo-login-btn"
              className="px-6 py-2.5 rounded-sm text-sm font-semibold text-white" style={{ background: C.teal }}>Sign in to continue</button>
          </div>
        </div>
      )}
      {/* Nav */}
      <nav className="h-14 px-6 flex items-center justify-between border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-2.5">
          <LogoIcon size={28} />
          <UnderaiterMark size="text-base" dark />
          <span className="text-xs px-2 py-0.5 rounded-sm ml-2 font-bold" style={{ background: 'rgba(226,75,74,0.15)', color: C.red }}>CAT Demo</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/demo')} className="text-xs px-4 py-1.5 rounded-sm" style={{ color: '#E0E6EC', border: '1px solid rgba(255,255,255,0.2)' }}>Full Demo</button>
          <button onClick={() => navigate('/')} className="text-xs px-4 py-1.5 rounded-sm font-semibold text-white" style={{ background: C.teal }}>Home</button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          {brand?.logo_data && <img src={brand.logo_data} alt={brand.insurer_name} className="h-10 w-auto mx-auto mb-3 rounded-sm px-2 py-1" style={{ background: 'rgba(255,255,255,0.92)' }} data-testid="cat-demo-brand-logo" />}
          <div className="flex items-center justify-center gap-3 mb-3">
            <AlertTriangle className="w-8 h-8" style={{ color: C.red }} />
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "Georgia, serif" }} data-testid="cat-demo-title">{brand?.insurer_name ? `${brand.insurer_name} — CAT Response` : 'CAT Mode Demo'}</h1>
          </div>
          <p className="text-sm" style={{ color: '#B0BAC5' }}>Catastrophe event claims processing — {brand?.insurer_name ? `${brand.insurer_name}'s surge capacity, powered by underAIter.com` : 'see how underAIter.com handles surge capacity in real time'}</p>
        </div>

        {/* Step 1: Activate */}
        {!isActive && (
          <div className="space-y-4 mb-8">
            {status?.status === 'recovery' && (
              <div className="p-4 rounded-sm flex flex-wrap items-center justify-between gap-3" style={{ background: 'rgba(29,158,117,0.1)', border: '1px solid rgba(29,158,117,0.3)' }} data-testid="cat-recovery-banner">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" style={{ color: C.green }} />
                  <div>
                    <p className="text-sm font-semibold text-white">Event ended — {status.event_name} is in recovery</p>
                    <p className="text-xs" style={{ color: '#B0BAC5' }}>New business holds released. Download the APRA report or declare a new event.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={downloadReport} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-bold text-white" style={{ background: C.saffron }} data-testid="cat-report-recovery">
                    <FileText className="w-3.5 h-3.5" /> APRA Event Report (PDF)
                  </button>
                  <button onClick={reset} className="px-3 py-2 rounded-sm text-xs font-medium" style={{ color: '#B0BAC5', border: '1px solid rgba(255,255,255,0.2)' }} data-testid="cat-reset-recovery">
                    Reset Demo
                  </button>
                </div>
              </div>
            )}
            <StepHeader num="1" title="Declare a Catastrophe Event" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {PRESETS.map(p => (
                <button key={p.name} onClick={() => activate(p)} disabled={activating}
                  className="p-4 rounded-sm text-left transition-all hover:scale-[1.01] disabled:opacity-50"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}
                  data-testid={`preset-${p.postcodes[0]}`}>
                  <p className="text-sm font-semibold text-white">{p.name}</p>
                  <p className="text-xs mt-1" style={{ color: '#B0BAC5' }}>{p.postcodes.length} postcodes | ${p.authority.toLocaleString()} auto-settle</p>
                </button>
              ))}
            </div>
            {activating && <p className="text-xs text-center animate-pulse" style={{ color: C.saffron }}>Activating CAT mode...</p>}
          </div>
        )}

        {/* Active Event */}
        {isActive && (
          <>
            {/* Event Banner */}
            <div className="p-5 rounded-sm mb-6" style={{ background: 'rgba(226,75,74,0.12)', border: '1px solid rgba(226,75,74,0.3)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded text-xs font-bold text-white animate-pulse" style={{ background: C.red }}>CAT ACTIVE</span>
                    <h2 className="text-lg font-bold text-white">{status.event_name}</h2>
                  </div>
                  <p className="text-xs mt-1" style={{ color: '#B0BAC5' }}>
                    Activated: {new Date(status.activated_at).toLocaleString()} | Postcodes: {status.affected_postcodes?.join(', ')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs" style={{ color: '#B0BAC5' }}>Auto-settlement elevated to</p>
                  <p className="text-xl font-bold font-mono text-white">${status.elevated_authority?.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Live Dashboard */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <MetricCard label="Total FNOLs" value={stats.total_claims || 0} icon={FileText} color={C.teal} />
              <MetricCard label="Auto-Settled" value={stats.settled || 0} icon={CheckCircle} color={C.green} />
              <MetricCard label="Open Claims" value={stats.open || 0} icon={Clock} color={C.amber} />
              <MetricCard label="Total Reserves" value={fmt$(stats.total_reserves || 0)} icon={DollarSign} color={C.red} />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <MetricCard label="Total Paid" value={fmt$(stats.total_paid || 0)} icon={DollarSign} color={C.green} />
              <MetricCard label="Entities Held" value={(status.entities_currently_held || 0).toLocaleString()} icon={Shield} color={C.amber} />
            </div>

            {/* Step 2: Flood FNOLs */}
            <div className="space-y-4 mb-8">
              <StepHeader num="2" title="Simulate FNOL Flood" />
              <div className="flex gap-3">
                {[10, 25, 50].map(n => (
                  <button key={n} onClick={() => flood(n)} disabled={flooding}
                    className="flex-1 py-3 rounded-sm text-sm font-bold text-white transition-all hover:scale-[1.01] disabled:opacity-50"
                    style={{ background: C.teal }}
                    data-testid={`flood-${n}`}>
                    {flooding ? <span className="animate-pulse">Processing...</span> : <><Zap className="w-4 h-4 inline mr-1" /> {n} FNOLs</>}
                  </button>
                ))}
              </div>
            </div>

            {/* Flood Results */}
            {floodResult && (
              <div className="p-5 rounded-sm mb-6" style={{ background: 'rgba(29,158,117,0.08)', border: '1px solid rgba(29,158,117,0.2)' }}>
                <h3 className="text-sm font-bold text-white mb-3">Flood Simulation Results</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div><p className="text-[10px] uppercase" style={{ color: '#B0BAC5' }}>Processed</p><p className="text-xl font-bold font-mono text-white">{floodResult.simulated}</p></div>
                  <div><p className="text-[10px] uppercase" style={{ color: '#B0BAC5' }}>Auto-Settled</p><p className="text-xl font-bold font-mono" style={{ color: C.green }}>{floodResult.auto_settled}</p></div>
                  <div><p className="text-[10px] uppercase" style={{ color: '#B0BAC5' }}>Total Quantum</p><p className="text-xl font-bold font-mono text-white">{fmt$(floodResult.total_quantum)}</p></div>
                  <div><p className="text-[10px] uppercase" style={{ color: '#B0BAC5' }}>Manual Review</p><p className="text-xl font-bold font-mono" style={{ color: C.amber }}>{floodResult.manual_required}</p></div>
                </div>
              </div>
            )}

            {/* APRA Event Report */}
            <div className="space-y-4 mb-8">
              <StepHeader num="3" title="APRA CAT Event Report" />
              <div className="p-5 rounded-sm" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span style={{ color: '#B0BAC5' }}>Event Name:</span> <span className="text-white font-medium">{status.event_name}</span></div>
                  <div><span style={{ color: '#B0BAC5' }}>Event ID:</span> <span className="text-white font-mono text-xs">{status.event_id?.slice(0, 8)}</span></div>
                  <div><span style={{ color: '#B0BAC5' }}>Activation Time:</span> <span className="text-white">{new Date(status.activated_at).toLocaleString()}</span></div>
                  <div><span style={{ color: '#B0BAC5' }}>Affected Postcodes:</span> <span className="text-white">{status.affected_postcodes?.length}</span></div>
                  <div><span style={{ color: '#B0BAC5' }}>Total Claims:</span> <span className="text-white font-mono">{stats.total_claims || 0}</span></div>
                  <div><span style={{ color: '#B0BAC5' }}>Claims Settled:</span> <span className="text-white font-mono">{stats.settled || 0}</span></div>
                  <div><span style={{ color: '#B0BAC5' }}>Total Reserves:</span> <span className="text-white font-mono">{fmt$(stats.total_reserves || 0)}</span></div>
                  <div><span style={{ color: '#B0BAC5' }}>Total Paid:</span> <span className="text-white font-mono">{fmt$(stats.total_paid || 0)}</span></div>
                  <div><span style={{ color: '#B0BAC5' }}>New Business Hold:</span> <span className="text-white">{status.new_business_hold ? 'Active' : 'Inactive'}</span></div>
                  <div><span style={{ color: '#B0BAC5' }}>Entities Held:</span> <span className="text-white font-mono">{(status.entities_currently_held || 0).toLocaleString()}</span></div>
                </div>
                <p className="text-[10px] mt-3" style={{ color: '#7A8A9A' }}>APRA Notification: Within 48 hours of activation per CPS 232. Reinsurer notification per treaty threshold.</p>
                <button onClick={downloadReport} className="mt-4 flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-sm font-bold text-white transition-all hover:scale-[1.01]" style={{ background: C.saffron }} data-testid="cat-report-download">
                  <FileText className="w-4 h-4" /> Generate APRA Event Report (PDF)
                </button>
              </div>
            </div>

            {/* Controls */}
            <div className="flex gap-3">
              <button onClick={endEvent} className="flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-sm font-bold text-white" style={{ background: C.red }} data-testid="cat-end-event">
                <Square className="w-4 h-4" /> End Event
              </button>
              <button onClick={reset} className="flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-sm font-medium" style={{ color: '#B0BAC5', border: '1px solid rgba(255,255,255,0.15)' }} data-testid="cat-reset">
                <RotateCcw className="w-4 h-4" /> Reset Demo
              </button>
            </div>
          </>
        )}

        {/* CTA */}
        <div className="mt-12 text-center border-t pt-8" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <button onClick={() => {
            const redirectUrl = window.location.origin + '/dashboard';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }} className="px-8 py-3 rounded-sm text-sm font-bold text-white" style={{ background: C.teal }}>
            Try Full Platform <ArrowRight className="w-4 h-4 inline ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
}

function StepHeader({ num, title }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: C.teal }}>{num}</span>
      <h3 className="text-sm font-semibold text-white uppercase tracking-wider">{title}</h3>
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, color }) {
  return (
    <div className="p-4 rounded-sm" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] uppercase tracking-wider" style={{ color: '#7A8A9A' }}>{label}</p>
        <Icon className="w-4 h-4" style={{ color: color || '#7A8A9A' }} />
      </div>
      <p className="text-xl font-bold font-mono" style={{ color: color || 'white' }}>{value}</p>
    </div>
  );
}
